import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import Pagination, { getPageSize } from '../../Pagination';
import type { AwDoc, AwDocKind, AwDocPage } from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, btn, card, dmy, errBox, inp, note, num, openBlob, pillGreen, pillGrey, pillRed, table, td, th } from '../ui';

// ── Sales: the client company's own documents, read-only (owner 2026-10-13) ─────────────────────
// Every row comes from /api/acct-workspace/docs/:kind, which the server scopes by the merchant row
// linked to this client (company_id = merchant.id — never a JOIN on invoices.client_id, which points
// at the merchant's own customer list). Voided documents are hidden server-side. No create, edit or
// void here: issuing a document is the company's own act in its portal, and numbering is theirs.

const DT: Record<string, string> = {
  invoice: 'aw_dt_invoice', proforma: 'aw_dt_proforma', tax_invoice: 'aw_dt_tax_invoice', tax_invoice_receipt: 'aw_dt_tax_invoice_receipt',
  credit_note: 'aw_dt_credit_note', debit_note: 'aw_dt_debit_note',
};
const r2 = (n: number) => Math.round(n * 100) / 100;
/** The portal's own per-row figure (ClientPortal `owing`, the owner console's OWING): what is still
 *  to be paid on an invoice. Re-mounted, not invented — the same two stored columns, same rounding. */
const owing = (d: AwDoc) => r2((Number(d.net_to_pay) || 0) - (Number(d.paid_amount) || 0));

const statusStyle = (s: string): React.CSSProperties => /paid|accepted|approved/i.test(s) ? pillGreen : /void|reject|cancel/i.test(s) ? pillRed : pillGrey;

export function DocsPane({ ctx, kind, title }: { ctx: AwCtx; kind: AwDocKind; title: string }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [data, setData] = useState<AwDocPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  // Thai tax documents only exist for a Thai-mode merchant. For any other the shell does not list the
  // submenu at all (menus.ts thaiOnly / visibleSubs — owner 2026-10-13: no grey menus), so this branch is
  // reached only in the moment between /summary arriving and the shell re-resolving the URL: the list is
  // not fetched and a plain line says why — never the grey "not in this version" card.
  const thaiOnly = kind === 'tax_invoice' || kind === 'note';
  const notApplicable = thaiOnly && !!ctx.summary && !ctx.summary.thaiTaxMode;

  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    if (notApplicable) return;
    let alive = true;
    api.awDocs(ctx.clientId, kind, { page, limit, q: qd })
      .then((d: AwDocPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, kind, page, limit, qd, ctx.reload, notApplicable]);

  if (notApplicable) return <div style={note}><i className="ti ti-info-circle" aria-hidden /> <b>{title}</b> — {t('aw_thai_only')}</div>;

  const openPdf = async (d: AwDoc) => {
    if (busy) return; setBusy(d.id); setErr('');
    try { openBlob(await api.awDocPdfBlobUrl(ctx.clientId, kind, d.id)); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const isReceipt = kind === 'receipt';
  const isQuote = kind === 'quotation';
  const showOwing = kind === 'invoice' || kind === 'tax_invoice';
  // Three families mix two doc_types each (invoice + proforma, tax invoice + receipt/tax invoice,
  // credit + debit note), so each row says which it is — a proforma must never read as a bill.
  const showType = kind === 'invoice' || kind === 'tax_invoice' || kind === 'note';
  const rows = data?.rows || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_search_ph')} style={{ ...inp, flex: '1 1 240px', maxWidth: 420 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
          : !rows.length ? <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_col_no')}</th>
                <th style={th}>{t('aw_col_date')}</th>
                {showType && <th style={th}>{t('aw_col_type')}</th>}
                <th style={th}>{t('aw_col_customer')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{isReceipt ? t('aw_col_net') : t('aw_col_total')}</th>
                <th style={th}>{t('aw_col_currency')}</th>
                {showOwing && <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_paid')}</th>}
                {showOwing && <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_outstanding')}</th>}
                <th style={th}>{t('aw_col_status')}</th>
                {!isQuote && <th style={th}>{t('aw_col_linked')}</th>}
                <th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(d => {
                // A PROFORMA is not a receivable (owner 2026-10-02, the AR card's rule): nothing is owed on
                // it, so Paid / Outstanding print '—' instead of a red figure that the Dashboard › Receivables
                // card in the same workspace rightly leaves out.
                const proforma = d.doc_type === 'proforma';
                const o = showOwing && !proforma ? owing(d) : 0;
                return (
                  <tr key={d.id}>
                    <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{d.doc_no}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(d.date)}</td>
                    {showType && <td style={{ ...td, whiteSpace: 'nowrap' }}>{DT[d.doc_type] ? t(DT[d.doc_type]) : d.doc_type}</td>}
                    <td style={td}>{d.client_name}</td>
                    {/* Receipts show what was received (net_to_pay) — never `amount`, which is gross of the buyer's WHT. */}
                    <td style={num}>{amt(isReceipt ? d.net_to_pay : d.total_amount)}</td>
                    <td style={td}>{d.currency}</td>
                    {showOwing && <td style={{ ...num, color: proforma ? 'var(--text-tertiary)' : undefined }} title={proforma ? t('aw_proforma_not_receivable') : undefined}>{proforma ? '—' : amt(d.paid_amount)}</td>}
                    {showOwing && <td style={{ ...num, color: o > 0 ? '#B42318' : 'var(--text-tertiary)', fontWeight: o > 0 ? 700 : 400 }} title={proforma ? t('aw_proforma_not_receivable') : undefined}>{o > 0 ? amt(o) : '—'}</td>}
                    {/* d.status is the document's stored state — data, shown as stored. */}
                    <td style={td}>{d.status ? <span style={statusStyle(d.status)}>{d.status}</span> : ''}</td>
                    {!isQuote && <td style={{ ...td, whiteSpace: 'nowrap' }}>{d.linked_no || ''}</td>}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => openPdf(d)} disabled={!!busy} title={t('aw_col_open_pdf')} style={{ ...btn, padding: '4px 9px', fontSize: 12, opacity: busy === d.id ? 0.6 : 1 }}>
                        <i className={busy === d.id ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('aw_col_open_pdf')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {/* The pager is driven by the page size the SERVER answered with (data.limit), not the one asked
          for: if the two ever differ, a pager counting pages at the client's size would hide the rows
          past the server's. The select still shows what the server used. */}
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      {/* The summary's live counters span a whole table. For quotations and receipts that IS this list;
          the invoices counter also holds tax invoices and notes (every doc_type of the invoices table),
          so it is not printed under Sales › Invoices, whose own total is shown above. */}
      {ctx.summary && (kind === 'quotation' || kind === 'receipt') && (
        <div style={note}>{title} · {t('aw_col_count')}: {kind === 'quotation' ? ctx.summary.counts.quotations : ctx.summary.counts.receipts}</div>
      )}
    </div>
  );
}
